<template>
	<component
		:is="button"
		class="button-base"
		v-bind="$props"
		@click="handleClick($event)"
		@mousedown="handleMouseDown($event)"
	>
		<span class="button-content">
			<slot />
		</span>
		<Icon
			v-if="loading && state === 'icon'"
			name="loading"
			size="medium"
			class="position-absolute"
			state="primary"
			type="outlined"
		/>
		<Icon
			v-if="loading && state !== 'icon' && loadingType === 'icon'"
			name="loading"
			size="18px"
			class="flow-btn-loading-icon"
			state="black"
			type="outlined"
			:effects="false"
		/>
	</component>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";
import Icon from "./../Icon/Icon.vue";

const SIZE = ["normal", "small", "x-small"] as const;
const TYPE = ["primary", "secondary", "error", "success", "warning", "default"] as const;
const STATE = ["filled", "outlined", "text", "icon", "full", "curved"] as const;
const LOADING = ["background", "icon"] as const;

export type ButtonType = typeof TYPE[number];
export type ButtonSize = typeof SIZE[number];
export type ButtonState = typeof STATE[number];
export type ButtonLoading = typeof LOADING[number];

/**
 * Buttons are interactive elements used for call to action.
 */
export default defineComponent({
	name: "Button",
	components: {
		Icon
	},
	props: {
		/**
		 * 'normal' | 'small' | 'x-small'
		 */
		size: {
			type: String as PropType<ButtonSize>,
			default: "normal",
			validator(value: ButtonSize) {
				return SIZE.includes(value);
			}
		},

		/**
		 * 'primary' | 'secondary'  | 'error' | 'success' | 'warning'  | 'default'
		 */
		type: {
			type: String as PropType<ButtonType>,
			default: "primary",
			validator(value: ButtonType) {
				return TYPE.includes(value);
			}
		},

		/**
		 * 'filled' | 'outlined'  | 'text' | 'icon' | 'full' | 'curved' <br/> NOTE : For icon state you must have only Icon component in default slot
		 */
		state: {
			type: String as PropType<ButtonState>,
			default: "filled",
			validator(value: ButtonState) {
				return STATE.includes(value);
			}
		},

		/**
		 * true | false
		 */
		disabled: {
			type: Boolean,
			default: false
		},

		/**
		 * true | false
		 */
		fullWidth: {
			type: Boolean,
			default: false
		},

		/**
		 * true | false
		 */
		loading: {
			type: Boolean,
			default: false
		},

		/**
		 * 'icon' | 'background'
		 */
		loadingType: {
			type: String as PropType<ButtonLoading>,
			default: "icon",
			validator(value: ButtonLoading) {
				return LOADING.includes(value);
			}
		}
	},
	// Workaround because the native button `type` property does not match the one we set on it
	data() {
		return {
			button: "button"
		};
	},
	methods: {
		handleClick(e: MouseEvent) {
			/**
			 * Click event.
			 *
			 * @event click
			 */
			this.$emit("click", e);
		},

		handleMouseDown(e: MouseEvent) {
			e.preventDefault();

			this.$emit("mousedown", e);
		}
	}
});
</script>
<style lang="scss">
@use "sass:math";

$white: #fff;

/** Dashbaord/Theme Colors  */
$background-colors: (
	"primary": var(--dashboard-primary),
	"secondary": var(--dashboard-secondary),
	"error": var(--dashboard-danger),
	"success": var(--dashboard-success),
	"warning": var(--dashboard-warning),
	"default": var(--dashboard-element)
);

$colors: (
	"primary": var(--dashboard-background),
	"secondary": var(--dashboard-background),
	"error": var(--dashboard-background),
	"success": var(--dashboard-background),
	"warning": var(--dashboard-background),
	"default": var(--dashboard-font)
);

$focus: var(--dashboard-primary);
$disabled: #fff;
$hover: #000;

$sizes: (
	"normal": 32px,
	"small": 20px,
	"x-small": 12px
);

$types: "primary", "secondary", "error", "success", "warning", "default";

.button-base {
	@each $type in $types {
		&[type="#{$type}"] {
			background-color: map-get($background-colors, $type);
			color: map-get($colors, $type);
			border: none;

			&[state="outlined"] {
				border: 1px solid map-get($background-colors, $type);
				background-color: transparent;
				color: map-get($background-colors, $type);

				&[type="default"] {
					border: 1px solid map-get($colors, $type);
					color: map-get($colors, $type);
				}
			}
			&[state="full"] {
				border-radius: inherit;
				min-height: inherit;
				height: 36px;
				max-width: unset;
				flex: 1 0 auto;
				> .button-content > div.icon-container:only-child {
					margin: 0px 0px 0px 0px;
				}
			}
			&[state="text"] {
				border: none;
				background-color: transparent;
				color: map-get($background-colors, $type);

				&[type="default"] {
					color: map-get($colors, $type);
				}
			}
			&[state="icon"] {
				> .icon-container {
					& > [type="outlined"] > svg {
						stroke: map-get($colors, $type);
					}

					& > [type="filled"] > svg {
						fill: map-get($colors, $type);
					}

					&:hover:not([disabled]) {
						& > [type="outlined"] > svg {
							stroke: map-get($colors, $type);
						}

						& > [type="filled"] > svg {
							fill: map-get($colors, $type);
						}
					}
					&:focus:not([disabled]),
					&:active:not([disabled]) {
						& > [type="outlined"] > svg {
							stroke: map-get($colors, $type);
						}
						& > [type="filled"] > svg {
							fill: map-get($colors, $type);
						}
						background-color: transparent;
					}

					&[name="loading"] > [type="filled"] > svg {
						fill: var(--dashboard-font-light);
					}
				}
			}
		}
	}
	&:not([loading]) {
		&:not(:focus) {
			&:active:not(:disabled),
			&:hover:not(:active):not(:disabled) {
				&:before {
					background-color: $hover;
				}
			}
		}

		&:focus:not(:disabled) {
			&:before {
				background-color: transparent;
				border: 2px solid $focus;
			}
		}
	}
	&[loading] {
		pointer-events: none;
	}
}

.button-base {
	position: relative;
	overflow: hidden;

	display: inline-flex;
	justify-content: center;
	align-items: center;

	box-sizing: border-box;

	padding-right: 16px;
	padding-left: 16px;
	padding-top: 0px;
	padding-bottom: 0px;

	max-width: 420px;

	font-size: 14px;
	text-transform: uppercase;
	font-weight: bold;
	letter-spacing: normal;
	word-spacing: normal;
	font-family: var(--body-font);

	outline: none;
	border-style: solid;
	border-image: none;

	transition: background-color 0.3s linear;

	&[fullWidth] {
		width: 100%;
	}
	cursor: pointer;

	@each $size-name, $size in $sizes {
		&[size="#{$size-name}"] {
			height: ($size + 4px);
			border-radius: math.div(($size + 4px), 2);

			&:not([loading]) {
				&:focus:not(:disabled) {
					&:before {
						border-radius: math.div(($size + 4px + 8px), 2);
					}
				}
			}
		}
	}
	&[size="x-small"] {
		font-size: 10px;
	}
	&[size="small"],
	&[state="full"] {
		font-size: 12px;
	}

	@each $type in $types {
		&[type="#{$type}"] {
			&[state="icon"] {
				> .icon-container {
					&:focus:not([disabled]),
					&:active:not([disabled]) {
						border-radius: 50%;
						outline: none;
					}
					margin-right: 0px;
				}
			}

			> .icon-container {
				height: auto;
				width: auto;
				margin-right: 8px;
			}
		}
	}
	&:not([loading]) {
		&:not(:focus) {
			&:active:not(:disabled),
			&:hover:not(:active):not(:disabled) {
				&:before {
					opacity: 0.5;
				}
			}

			&:active:not(:disabled) {
				&:before {
					opacity: 0.2;
				}
			}

			&:hover:not(:active):not(:disabled) {
				&:before {
					opacity: 0.15;
				}
			}
		}

		&:focus:not(:disabled) {
			overflow: visible;
			&:before {
				position: absolute;
				content: "";
				top: -4px;
				left: -4px;
				right: -4px;
				bottom: -4px;
				outline: none;
				opacity: 1;
				width: unset;
				height: unset;
			}

			&[state="outlined"] {
				&:before {
					top: -6px;
					left: -6px;
					right: -6px;
					bottom: -6px;
				}
			}
		}
	}

	&[state="curved"] {
		border-radius: 4px;
	}
	/**
* in icon state circular button displayed with Icon inside it
*/
	&[state="icon"] {
		border-radius: 50%;
		padding: 0px;
		width: 36px;
		height: 36px;
		flex-shrink: 0;

		&[size="small"] {
			width: 24px;
			height: 24px;
		}
		&[size="x-small"] {
			width: 16px;
			height: 16px;
		}

		&:not([loading]) {
			&:focus:not(:disabled) {
				&:before {
					border-radius: 50%;
				}
			}
		}

		> .button-content {
			> *,
			> :first-child {
				margin: 0px 0px 0px 0px;
			}
			> div.icon-container:only-child {
				margin: 0px 0px 0px 0px;
			}
		}
	}
	/**
	* :before used to disable button
 */
	&:disabled {
		cursor: not-allowed;
		filter: opacity(0.5) !important;
		* {
			cursor: not-allowed !important;
		}
		&:before {
			position: absolute;
			content: "";

			opacity: 0.5;
		}
	}

	/**
* :after used to background filled type loading
 */
	&:after {
		content: "";
		position: absolute;
		transform-origin: 0% 0%;
		transform: scaleX(0);
		background-color: #000;
		opacity: 0.2;
	}
	/**
* :before is used for disabled , hover, focus
*/
	&:before {
		background-color: transparent;
		position: absolute;
		content: "";
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		width: 100%;
		height: 100%;
		transition: opacity 0.2s linear;
		opacity: 0;
	}

	&[loading][loadingType="background"]:not([state="icon"]) {
		&:after {
			top: 0;
			right: 0;
			left: 0;
			bottom: 0;
			transition: transform 20s linear;
			transform: scaleX(1);
		}
	}

	&[loading][loadingType="icon"]:not([state="icon"]) {
		> .button-content {
			margin-left: 26px;
			filter: contrast(0.6);
		}
	}

	> .button-content {
		z-index: 1;
		display: flex;
		align-items: center;
		justify-content: center;
		white-space: nowrap;
		width: 100%;
		text-overflow: ellipsis;
		overflow: hidden;

		> *:not(:only-child) {
			margin: 0px 8px 0px 0px;
		}

		> :first-child:not(:only-child) {
			margin: 0px 8px 0px 0px;
		}
		> :last-child:not(:only-child) {
			margin: 0px 0px 0px 0px;
		}

		> div.icon-container:only-child {
			margin: 0px 8px 0px 0px;
		}
	}
	&.button-meta {
		position: absolute;

		&[state="icon"] {
			width: 20px;
			height: 20px;
			right: -4px;
			bottom: -4px;
		}
	}
	> div.flow-btn-loading-icon {
		margin-right: 0px !important;
		margin-left: 8px;
	}
	&[state="filled"]:not([type="default"]),
	&[state="icon"]:not([type="default"]) {
		div.icon-container > div.icon[type="filled"] svg *[fill^="white"] {
			fill: var(--dashboard-background);
		}
		div.icon-container > div.icon[type="outlined"] svg {
			stroke: var(--dashboard-background);
		}
	}
}

.button-text-slide {
	&-enter {
		transform: translateX(100%);
	}
	&-leave-to {
		transform: translateX(-100%);
	}
	&-enter-active,
	&-leave-active {
		width: 100%;
		transition: transform 0.3s linear;
	}
}
</style>
